/**
 * Spelling (docs/PARITY-STEP-6-PLAN.md section 6): words checked against LanguageTool's
 * English dictionaries, the desktop's own, exported by its `SpellingDataBuilder` - US or
 * UK English, with LanguageTool's additions, its never-flagged words and its always-flagged
 * ones - in the browser, never on a server. How words are split and when one counts as
 * known follows what the desktop's checker does, measured against
 * `schema/fixtures/spelling-cases.json`; `AcceptedWords` and the filtering of what's shown
 * (`Checker.shown`) are ported exactly.
 */

export type Dialect = 'US' | 'UK';
export const DIALECTS: Readonly<Record<Dialect, string>> = { US: 'English (US)', UK: 'English (UK)' };

/** One misspelling in a paragraph: the characters `[start, end)`, the word, and what could replace it, best first. */
export interface SpellingIssue {
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly suggestions: readonly string[];
}

const LETTER = /\p{L}/u;
const isUpper = (c: string) => /\p{Lu}/u.test(c);
const isLower = (c: string) => /\p{Ll}/u.test(c);
/** A word's characters: letters, marks, digits, and apostrophes and hyphens inside it. */
const WORD_CHAR = /[\p{L}\p{M}\p{N}'’\-‐‑]/u;
const APOSTROPHES = /['’]/u;
const HYPHENS = /[-‐‑]/u;

/** Web addresses and email addresses, which are never flagged. */
const UNCHECKED = /\b(?:https?:\/\/|www\.)[^\s<>"]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+/giu;

const capitalize = (w: string) => (w === '' ? w : w[0]!.toUpperCase() + w.slice(1));

/** The desktop's spelling dictionary for one dialect, read from `en-spelling.tsv` (uncompressed). */
export class SpellingDictionary {
  /** Every accepted form, as written. */
  private readonly words = new Set<string>();
  /** Lower case to the dictionary's forms and how common each is, for suggestions. */
  private readonly byLower = new Map<string, { word: string; frequency: number }[]>();
  private readonly prohibited = new Set<string>();
  private readonly pairs: [string, string][] = [];

  constructor(
    text: string,
    readonly dialect: Dialect,
  ) {
    const column = dialect === 'US' ? 2 : 3;
    const mine = dialect === 'US' ? 'US' : 'GB';
    for (const raw of text.split('\n')) {
      if (raw === '' || raw[0] === '#') continue;
      const f = raw.replace(/\r$/, '').split('\t');
      if (f[0] === 'W') {
        const frequency = f[column];
        if (frequency !== undefined && frequency !== '-') this.add(f[1]!, Number(frequency));
      } else if (f[0] === 'A') {
        if (f[1] === '*' || f[1] === mine) this.add(f[2]!, 0);
      } else if (f[0] === 'I') {
        // A phrase is let through word by word.
        for (const word of f[1]!.split(' ')) if (word !== '') this.words.add(word);
      } else if (f[0] === 'P') {
        this.prohibited.add(f[1]!);
      } else if (f[0] === 'R' && f[1] === mine) {
        this.pairs.push([f[2]!, f[3]!]);
      }
    }
  }

  private add(word: string, frequency: number): void {
    this.words.add(word);
    const key = word.toLowerCase();
    const list = this.byLower.get(key) ?? [];
    list.push({ word, frequency });
    this.byLower.set(key, list);
  }

  private has(word: string): boolean {
    return this.words.has(word) && !this.prohibited.has(word);
  }

  private frequencyOf(word: string): number {
    return Math.max(0, ...(this.byLower.get(word.toLowerCase()) ?? []).map((f) => f.frequency));
  }

  /** Whether a word as written is spelled right: as it is, or capitalized from a lower-case word, or in capitals. */
  knows(word: string): boolean {
    const w = word.replace(/’/gu, "'");
    if (this.has(w)) return true;
    const lower = w.toLowerCase();
    // "The" at a sentence's start; "THE KEEP" in capitals.
    if (isUpper(w[0]!) && this.has(lower)) return true;
    if (w.length > 1 && w === w.toUpperCase() && (this.has(lower) || this.has(capitalize(lower)))) return true;
    if (w.length > 1 && w === w.toUpperCase()) {
      for (const form of this.byLower.get(lower) ?? []) if (this.has(form.word)) return true;
    }
    return false;
  }

  /** What could replace a misspelling, best first: one edit away, else two, likelier words first, in its capitals. */
  suggest(word: string, limit = 8): string[] {
    const lower = word.replace(/’/gu, "'").toLowerCase();
    const caps = word.length > 1 && word === word.toUpperCase();
    const found = new Map<string, { distance: number; frequency: number }>();
    const consider = (candidate: string, distance: number) => {
      for (const form of this.byLower.get(candidate) ?? []) {
        if (!this.has(form.word) || form.word === word) continue;
        // An acronym ("BER") isn't offered for an ordinary word.
        if (!caps && form.word.length > 1 && form.word === form.word.toUpperCase()) continue;
        const known = found.get(form.word);
        if (!known || distance < known.distance) found.set(form.word, { distance, frequency: form.frequency });
      }
    };
    // Accents are ignored first: "über" is "Uber" before anything an edit away.
    const bare = lower.normalize('NFD').replace(/\p{M}/gu, '');
    if (bare !== lower) consider(bare, 0.5);
    // Two words run together: "Silverkeep" is "Silver keep", when both are words.
    const splits: { words: string; frequency: number }[] = [];
    for (let at = 3; at <= word.length - 3; at++) {
      const left = word.slice(0, at);
      const right = word.slice(at);
      if (/^\p{L}+$/u.test(left) && /^\p{L}+$/u.test(right) && this.knows(left) && this.knows(right) && this.has(right.toLowerCase())) {
        splits.push({ words: `${left} ${right.toLowerCase()}`, frequency: Math.min(this.frequencyOf(left), this.frequencyOf(right)) });
      }
    }
    splits.sort((x, y) => y.frequency - x.frequency);
    const first = edits(lower);
    for (const e of first) consider(e, 1);
    for (const [from, to] of this.pairs) {
      for (let at = lower.indexOf(from); at >= 0; at = lower.indexOf(from, at + 1)) consider(lower.slice(0, at) + to + lower.slice(at + from.length), 1);
    }
    if (found.size === 0 && lower.length <= 20) {
      for (const e of first) for (const e2 of edits(e)) consider(e2, 2);
    }
    const ranked = [...found.entries()].sort((a, b) => a[1].distance - b[1].distance || b[1].frequency - a[1].frequency || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    const capital = isUpper(word[0]!) && !caps;
    const out: string[] = [];
    const add = (w: string) => {
      const shaped = caps ? w.toUpperCase() : capital && isLower(w[0]!) ? capitalize(w) : w;
      if (!out.includes(shaped) && out.length < limit) out.push(shaped);
    };
    // The words an edit away, then the splits, then those two edits away.
    for (const [w, f] of ranked) if (f.distance <= 1) add(w);
    for (const split of splits.slice(0, 2)) if (out.length < limit) out.push(split.words);
    for (const [w, f] of ranked) if (f.distance > 1) add(w);
    return out;
  }

  /** Every misspelling in a paragraph's text. */
  check(paragraph: string): SpellingIssue[] {
    const issues: SpellingIssue[] = [];
    const skip: [number, number][] = [];
    for (const m of paragraph.matchAll(UNCHECKED)) skip.push([m.index, m.index + m[0].length]);
    let i = 0;
    while (i < paragraph.length) {
      if (!WORD_CHAR.test(paragraph[i]!)) {
        i++;
        continue;
      }
      let j = i;
      while (j < paragraph.length && WORD_CHAR.test(paragraph[j]!)) j++;
      this.checkToken(paragraph, i, j, skip, issues);
      i = j;
    }
    return issues;
  }

  private checkToken(paragraph: string, from: number, to: number, skip: readonly [number, number][], issues: SpellingIssue[]): void {
    // Apostrophes and hyphens at its ends aren't part of it.
    let a = from;
    let b = to;
    while (a < b && !/[\p{L}\p{M}\p{N}]/u.test(paragraph[a]!)) a++;
    while (b > a && !/[\p{L}\p{M}\p{N}]/u.test(paragraph[b - 1]!)) b--;
    if (a === b) return;
    if (skip.some(([s, e]) => a < e && b > s)) return;
    const token = paragraph.slice(a, b);
    if (/\p{N}/u.test(token) || !LETTER.test(token)) return;
    // A word with an apostrophe in front ('twas, ’tis) isn't checked, as the desktop's checker doesn't.
    if (a > from && APOSTROPHES.test(paragraph[a - 1]!)) return;
    if (this.knows(token)) return;
    // Possessives: Tomas's, the guards'.
    const possessive = /^(.+?)['’]s$/u.exec(token);
    if (possessive && this.knows(possessive[1]!)) return;
    // Hyphenated or with an apostrophe inside (d'Artagnan): each part on its own.
    if (HYPHENS.test(token) || APOSTROPHES.test(token)) {
      let at = a;
      for (const part of token.split(/([-‐‑'’])/u)) {
        if (part.length === 1 && /[-‐‑'’]/u.test(part)) {
          at += 1;
          continue;
        }
        if (part !== '' && LETTER.test(part) && part.length > 1 && !this.knows(part)) issues.push({ start: at, end: at + part.length, text: part, suggestions: this.suggest(part) });
        at += part.length;
      }
      return;
    }
    issues.push({ start: a, end: b, text: token, suggestions: this.suggest(token) });
  }
}

const ALPHABET = "abcdefghijklmnopqrstuvwxyz'-";

/** Every string one edit away: a letter deleted, swapped with the next, changed, or added. */
function edits(word: string): Set<string> {
  const out = new Set<string>();
  for (let i = 0; i <= word.length; i++) {
    const left = word.slice(0, i);
    const right = word.slice(i);
    if (right) out.add(left + right.slice(1));
    if (right.length > 1) out.add(left + right[1] + right[0] + right.slice(2));
    for (const c of ALPHABET) {
      if (right) out.add(left + c + right.slice(1));
      out.add(left + c + right);
    }
  }
  out.delete(word);
  return out;
}

// ---- what's accepted, and what's shown (`AcceptedWords`, `Checker.shown`) ----

const isLetterOrDigit = (c: string) => /[\p{L}\p{N}]/u.test(c);

/** `AcceptedWords.trim`: no punctuation in front, none behind but an apostrophe. */
function trimWord(word: string): string {
  let start = 0;
  let end = word.length;
  while (start < end && !isLetterOrDigit(word[start]!)) start++;
  while (end > start && !isLetterOrDigit(word[end - 1]!) && word[end - 1] !== "'" && word[end - 1] !== '’') end--;
  return word.slice(start, end);
}

/** Optimal-string-alignment distance: insertions, deletions, substitutions, and swapped neighbours. */
export function editDistance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) => Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + 1);
    }
  }
  return d[a.length]![b.length]!;
}

/**
 * The words a check lets through (`AcceptedWords`): every name the realm knows, whole and
 * word by word, the realm's and your own dictionaries, and what was ignored in this scene.
 * Matching ignores case and a trailing possessive.
 */
export class AcceptedWords {
  private readonly words = new Set<string>();
  private readonly suggestable = new Map<string, string>();
  private readonly ignored = new Set<string>();

  constructor(names: readonly string[], dictionary: readonly string[], ignoredKeys: readonly string[]) {
    for (const name of names) {
      for (const word of name.trim().split(/[\s ]+/u)) {
        const bare = trimWord(word);
        if (bare === '') continue;
        this.add(bare);
        for (const part of bare.split(/[-‐‑]/u)) if (part !== '') this.add(part);
      }
    }
    for (const word of dictionary) {
      const bare = trimWord(word);
      if (bare !== '') this.add(bare);
    }
    for (const key of ignoredKeys) if (key.trim() !== '') this.ignored.add(key.trim().toLowerCase());
  }

  private add(word: string): void {
    const key = word.toLowerCase();
    this.words.add(key);
    if (!this.suggestable.has(key)) this.suggestable.set(key, word);
  }

  accepts(word: string): boolean {
    const key = trimWord(word).toLowerCase();
    if (key === '') return false;
    if (this.words.has(key)) return true;
    for (const suffix of ["'s", '’s', "'", '’']) if (key.endsWith(suffix) && this.words.has(key.slice(0, -suffix.length))) return true;
    return false;
  }

  /** Whether an issue is let through: an accepted word, or ignored here (`ProofIssue.ignoreKey`). */
  letsThrough(issue: SpellingIssue): boolean {
    return this.ignored.has(ignoreKey(issue).toLowerCase()) || this.accepts(issue.text);
  }

  /** Accepted words that look like a slip of `typo`, closest first, at most three. */
  lookalikes(typo: string): string[] {
    const key = trimWord(typo).toLowerCase();
    if (key.length < 4) return [];
    const limit = key.length >= 7 ? 2 : 1;
    const near: string[] = [];
    for (let distance = 1; distance <= limit; distance++) {
      for (const [word, shown] of this.suggestable) {
        if (Math.abs(word.length - key.length) <= distance && editDistance(key, word) === distance && !near.includes(shown)) near.push(shown);
      }
    }
    return near.slice(0, 3);
  }
}

/** What Ignore remembers for a misspelling: the word, so every occurrence is let through. */
export const ignoreKey = (issue: Pick<SpellingIssue, 'text'>) => issue.text.trim().toLowerCase();

const MAX_SUGGESTIONS = 5;

/** What's shown of a paragraph's misspellings (`Checker.shown`): less what's accepted, the realm's lookalikes first. */
export function shownIssues(raw: readonly SpellingIssue[], accepted: AcceptedWords): SpellingIssue[] {
  const shown: SpellingIssue[] = [];
  for (const issue of raw) {
    if (accepted.letsThrough(issue)) continue;
    const suggestions = accepted.lookalikes(issue.text);
    for (const s of issue.suggestions) if (!suggestions.includes(s)) suggestions.push(s);
    shown.push({ ...issue, suggestions: suggestions.slice(0, MAX_SUGGESTIONS) });
  }
  return shown;
}
